import type { OrderId } from '../../../graphql';
import type { GetIncomeForLoadedAmount } from '../../shipment/income/income';
import type { OmitFunctions, ScheduleData } from '../Core';
import { DeriveCapacitiesAndTimes } from '../DeriveCapacitiesAndTimes';
import type {
  CalculationConfig,
  ScheduleDataContext,
} from '../ScheduleDataContext';
import type { Events } from '../TimeData';
import { isDischargeEventTimeData, isLoadEventTimeData } from '../TimeData';
import type { TimeData } from '../TimeData';
import type { Vessel } from '../Vessel';
import { computeIncomePrioritizedFillRatios } from './incomePrioritizedFill';

/** The context fields DeriveCapacitiesAndTimes reads. Matches its declared Pick. */
type DeriveContext = Pick<
  ScheduleDataContext,
  | 'coasById'
  | 'distances'
  | 'portCostEstimateMaps'
  | 'voyageExclusions'
  | 'locations'
  | 'currentUser'
>;

type ApplyFreightPrioritizedFillParams<EventType extends Events> = {
  vessel: Vessel;
  /** The schedule (events) to re-derive with the fill overlay. */
  schedule: EventType[];
  context: DeriveContext;
  config: OmitFunctions<CalculationConfig>;
  now: number;
  /** The base derive (no overlay). Returned unchanged when nothing is raisable or the fill is empty. */
  baseResult: ScheduleData<EventType>;
  getIncomeForLoadedAmount: GetIncomeForLoadedAmount;
  /** Orders the fill may raise from band-floor toward band-max. */
  raisableOrderIds: ReadonlySet<OrderId>;
  /** Committed path: let the fill reduce cargo below band minimum, income-ordered, when the set does
   * not fit at minimum. Always off for suggestions (they exclude such sets instead). */
  allowReduceBelowMin?: boolean;
  /** Whether the re-derived result should include vessel operating costs.
   * Inherit from the base derivation: false on COA/Spot timelines (the vessel is
   * the shipowner's, so its costs are not the charterer's), true on TC/vessel
   * timelines. Defaults to true so suggestion/estimator callers are unchanged. */
  calculateCosts?: boolean;
};

/**
 * Re-derive a voyage with the income-prioritized fill applied through `explicitLoadRatios`, mirroring
 * the suggestion sandwich (getSuggestions): compute per-order ratios from marginal freight income,
 * then re-derive once with the overlay. The overlay is a transient local Map keyed by OrderId,
 * consumed only by this re-derive and never written to the TimeDataCache, so no committed scenario
 * event and no other group can be affected.
 *
 * Returns the base derive unchanged when nothing is raisable and no reduction is requested, or when
 * the fill yields no ratios. The base derive already enforces the port-draft mass ceiling, so the
 * re-derive is self-consistent with it and needs no separate draft-violation discard.
 */
export function applyFreightPrioritizedFill<EventType extends Events>({
  vessel,
  schedule,
  context,
  config,
  now,
  baseResult,
  getIncomeForLoadedAmount,
  raisableOrderIds,
  allowReduceBelowMin = false,
  calculateCosts = true,
}: ApplyFreightPrioritizedFillParams<EventType>): ScheduleData<EventType> {
  if (raisableOrderIds.size === 0 && !allowReduceBelowMin) return baseResult;

  const ratios = computeIncomePrioritizedFillRatios(
    baseResult.timeData,
    vessel,
    getIncomeForLoadedAmount,
    raisableOrderIds,
    allowReduceBelowMin,
  );
  if (ratios.size === 0) return baseResult;

  // This result replaces the base derive on the surfaces that render it (committed timeline,
  // estimator), so it has to carry the port call swap eligibility too — skipping the swap
  // computation here left every flag at its initialised false and silently withdrew the port
  // rotation controls from exactly the multi-parcel voyages the fill applies to (SEABER-6790).
  return DeriveCapacitiesAndTimes(
    vessel,
    schedule,
    context,
    config,
    {
      calculateCosts,
      explicitLoadRatios: ratios,
    },
    now,
  );
}

/**
 * Committed raisable set (design §4): an order may be raised iff it has a genuine band and is
 * co-loaded with at least one other banded order contending for the same voyage.
 *
 * User-pinned amount: a user pins a cargo quantity through cargoAmount + marginAmount/marginUnit,
 * which calculateMassAndVolumeRanges turns into the band. Pinning an exact quantity (no margin)
 * collapses mass.min == mass.max, so the genuine-band test below already excludes it — there is no
 * separate loaded-amount override on a committed order for the fill to move (ScheduledOrder carries
 * only timing overrides, no amount). So no extra override check is needed.
 *
 * Well-defined-per-order-freight exclusion (orders whose income is only voyage/shipment-level) stays
 * a TODO: the fill already pins such an order to its floor — it sums only `orderId !== null` income,
 * so a voyage-level-only order ranks at marginal 0 and the greedy never raises it. Excluding it here
 * would only save a re-derive, and clean detection would need the resolved per-order freight (direct
 * VC freight plus COA price/route/assortment matching) threaded into this timeData-only signature,
 * which the committed wiring cannot supply without changes out of scope here. Lump-sum per-order
 * freight legitimately ranks at 0 and must stay eligible, so it is intentionally NOT excluded.
 *
 * Returns the empty set whenever fewer than two banded orders co-load, which is the gate that keeps
 * the single-parcel majority on today's default fill.
 */
export function computeCommittedRaisableOrderIds(
  timeData: TimeData[],
): ReadonlySet<OrderId> {
  const banded = collectBandedOrderIds(timeData);
  // Contention gate: a lone banded order already fills to capacity on its own; the freight ranking
  // only matters when two or more banded orders compete for the same binding capacity.
  return banded.size < 2 ? new Set() : banded;
}

/** Orders both loaded and discharged on this voyage, tagged with whether they carry a genuine margin
 * band. Only these orders occupy capacity and contend for it. */
function collectContendingOrders(
  timeData: TimeData[],
): { orderId: OrderId; banded: boolean }[] {
  const dischargedOrderIds = new Set<OrderId>();
  for (const td of timeData) {
    if (isDischargeEventTimeData(td)) dischargedOrderIds.add(td.event.order.id);
  }
  const seen = new Set<OrderId>();
  const contending: { orderId: OrderId; banded: boolean }[] = [];
  for (const td of timeData) {
    if (!isLoadEventTimeData(td)) continue;
    const { order } = td.event;
    if (!dischargedOrderIds.has(order.id) || seen.has(order.id)) continue;
    seen.add(order.id);
    const banded =
      order.cargoRanges.mass.max > order.cargoRanges.mass.min ||
      order.cargoRanges.volume.max > order.cargoRanges.volume.min;
    contending.push({ orderId: order.id, banded });
  }
  return contending;
}

/** The banded co-loaded orders of a voyage, ungated — the raisable set the reduce path hands the fill
 * so a single banded order is still returned to the derive (the ≥2 gate is a raise-only concern). */
export function collectBandedOrderIds(
  timeData: TimeData[],
): ReadonlySet<OrderId> {
  return new Set(
    collectContendingOrders(timeData)
      .filter((o) => o.banded)
      .map((o) => o.orderId),
  );
}

/** Count of orders both loaded and discharged on this voyage — the number contending for capacity. */
export function countContendingOrders(timeData: TimeData[]): number {
  return collectContendingOrders(timeData).length;
}
